// ตัวกลางเรียก API ของเราเอง
//
// baseURL เป็น /app-api/v1 ไม่ใช่ /api/v1 เพราะ /api บนโดเมนนี้ถูก reverse proxy
// ไปที่ Inbox Center ทั้งก้อน (ดู docs/REFACTOR-PLAN.md ข้อ 3)
import axios, { AxiosError } from "axios";

export const api = axios.create({
  baseURL: "/app-api/v1",
  timeout: 60_000,
  headers: { Accept: "application/json" },
});

/**
 * แนบ token ของผู้ใช้ไปทุก request ของ API เราเอง
 *
 * server ต้องรู้ว่าใครเรียกถึงจะกรองแบรนด์ตามสิทธิ์และกันหน้า Setting ได้
 * ทำที่นี่ที่เดียวแทนการไล่ใส่ทีละจุด — จุดที่ลืมใส่จะกลายเป็น 401/403 แบบงง ๆ
 * (relay ใส่ header เองอยู่แล้ว ค่าเดียวกัน จึงไม่ชนกัน)
 */
api.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    try {
      const raw = localStorage.getItem("wazzup_session");
      const token = raw ? (JSON.parse(raw) as { access_token?: string }).access_token : "";
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {
      /* session พัง = ปล่อยไปโดยไม่มี token แล้วให้ server ตัดสิน */
    }
  }
  return config;
});

/**
 * relay = เส้นที่ส่ง token ต่อไปให้ระบบปลายทาง (Inbox Center / KOL / Biddable)
 * 401 จากเส้นพวกนี้หมายถึง "ปลายทางไม่รับ token ใบนี้" ไม่ใช่ "session เราตาย"
 * จึงต้องไม่ล้าง session — ไม่งั้นจะวนเข้า-ออก login ไม่จบเมื่อปลายทางบางตัวไม่รับ
 */
const RELAY_PREFIXES = [
  "/inbox",
  "/kol",
  "/biddable",
  /**
   * /wazzup/* ทั้งหมดเป็นการถามระบบอื่น (Wazzup / IAM) ไม่ใช่การตรวจ session ของเรา
   * 401 จากเส้นพวกนี้แปลว่า "ปลายทางไม่รับ token ใบนี้" ซึ่งเกิดได้ตลอดตั้งแต่ย้ายมา SSO
   * (token ออกโดย IAM แต่ Wazzup เป็นคนตรวจ) — ล้าง session ที่นี่จะเด้งคนที่ login
   * สำเร็จออกทันทีที่เปิดหน้าไหนก็ตามที่บังเอิญเรียกรายชื่อพนักงาน
   *
   * session ของเราตายจริงหรือไม่ ให้ตัดสินจาก API ของเราเอง (brands / groups / campaigns / …)
   */
  "/wazzup/",
];

const isRelay = (url = "") => RELAY_PREFIXES.some((p) => url.startsWith(p));

/**
 * 401 จาก API ของเราเอง = credential ใช้ไม่ได้แล้ว → ล้าง session แล้วไปหน้า login
 * พร้อมจำหน้าที่ค้างไว้ใน ?next= (ทำที่นี่ที่เดียว ทุกหน้าจึงไม่ต้องเช็คเอง)
 */
api.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    const url = error.config?.url ?? "";
    if (error.response?.status === 401 && !isRelay(url)) {
      localStorage.removeItem("wazzup_session");
      if (!location.pathname.startsWith("/login")) {
        const next = encodeURIComponent(location.pathname + location.search);
        location.replace(`/login?next=${next}&expired=1`);
      }
    }
    return Promise.reject(error);
  },
);

/** ข้อความ error จาก server อยู่ใน field `error` — ดึงออกมาให้ UI แสดงได้ตรง ๆ */
export function apiErrorMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const body = e.response?.data as { error?: string } | undefined;
    if (body?.error) return body.error;
    if (e.code === "ECONNABORTED") return "หมดเวลารอ — ปลายทางตอบช้ากว่าปกติ";
    if (!e.response) return "ต่อ server ไม่ได้ — เช็คการเชื่อมต่อแล้วลองอีกครั้ง";
    return `ผิดพลาด HTTP ${e.response.status}`;
  }
  return e instanceof Error ? e.message : "เกิดข้อผิดพลาดที่ไม่รู้สาเหตุ";
}
